import { useFormikContext } from 'formik';
import {
  MutableRefObject,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  RealtimePositionEvent,
  RealtimePositionResult,
} from '../../../../api/hooks/binding/realtime-position.binding';
import { useMachineContext } from '../../../../pages/machine/machine-context-provider';
import { QNear, Waypoint } from '../../../../util/machine-variable.utils';
import { useFlowItemFormContext } from '../component/flow-item/edit/flow-item-form.context';
import {
  FlowItemSharedParameterRef,
  TeachIOMapping,
  ToolConfig,
} from '../model/flow.types';
import { RobotTeachError, useCanRobotTeachWithAlert } from './flow-teach.hooks';
import { useWaypointCaptureBindings } from './waypoint-capture-bindings.hooks';

export interface WaypointCaptureContextValue {
  ioMapping: TeachIOMapping;
  toolConfig: ToolConfig;
}

export const WaypointCaptureContext = createContext<
  WaypointCaptureContextValue | undefined
>(undefined);

export interface WaypointCaptureConfig {
  innerRef?: MutableRefObject<WaypointCaptureRef | undefined>;
  delayPress?: number;
  delayLongPress?: number;
  onPress?: (position: RealtimePositionEvent | null) => void;
  onLongPress?: () => void;
  onTeachError?: (error: RobotTeachError) => void;
}

export interface WaypointCaptureRef {
  realtimePosition: RealtimePositionResult | undefined;
  forcePress: () => void;
  forceLongPress: () => void;
}

export interface WaypointCaptureContextProviderProps
  extends WaypointCaptureContextValue {
  children: ReactElement;
}

export interface WaypointCaptureProps {
  onCapture?: (waypoint: Waypoint, qnear: QNear) => void;
  onLongPress?: () => void;
  innerRef?: MutableRefObject<WaypointCaptureRef | undefined>;
  onTeachError?: (error: RobotTeachError) => void;
}

interface WaypointFormCaptureProps extends WaypointCaptureProps {
  autoSubmitForm?: boolean;
}

const WaypointCaptureContextProvider = (
  props: WaypointCaptureContextProviderProps
): ReactElement | null => {
  const { ioMapping, toolConfig } = props;

  const value = useMemo<WaypointCaptureContextValue>(() => {
    return {
      ioMapping,
      toolConfig,
    };
  }, [ioMapping, toolConfig]);

  if (value) {
    return (
      <WaypointCaptureContext.Provider value={value}>
        {props.children}
      </WaypointCaptureContext.Provider>
    );
  }

  return null;
};

const useWaypointCaptureContext = () => {
  return useContext(WaypointCaptureContext) as WaypointCaptureContextValue;
};

const useWaypointCaptureBinding = (config: WaypointCaptureConfig): void => {
  const teachError = useCanRobotTeachWithAlert();

  const { ioMapping, toolConfig } = useWaypointCaptureContext();
  const { machine } = useMachineContext();
  const {
    innerRef,
    onTeachError,
    delayPress,
    delayLongPress,
    onLongPress,
    onPress,
  } = config;

  const machineId = machine.id;

  useEffect(() => {
    if (teachError && typeof onTeachError === 'function') {
      onTeachError(teachError);
    }
  }, [teachError, onTeachError]);

  // Delegate to manufacturer-specific bindings
  useWaypointCaptureBindings({
    machineId,
    ioMapping,
    toolConfig,
    delayPress,
    delayLongPress,
    onPress,
    onLongPress,
    innerRef,
  });
};

const useWaypointCapture = (props: WaypointCaptureProps): void => {
  const { onCapture, onLongPress, innerRef, onTeachError } = props;

  useWaypointCaptureBinding({
    innerRef,
    onPress: (position) => {
      if (position) {
        if (typeof onCapture === 'function') {
          onCapture(position.waypoint, position.jointPositions);
        }
      }
    },
    onLongPress,
    onTeachError,
  });
};

const useUpdateFlowItemFormOnWaypointCapture = ({
  autoSubmitForm = false,
}): ((waypoint: Waypoint, qnear: QNear) => void) => {
  const { setParameterValue } = useFlowItemFormContext();
  const { submitForm } = useFormikContext();

  const onCapture = useCallback(
    (waypoint: Waypoint, qnear: QNear) => {
      if (waypoint && qnear) {
        setParameterValue(FlowItemSharedParameterRef.WAYPOINT, waypoint, false);
        setParameterValue(FlowItemSharedParameterRef.QNEAR, qnear);

        if (typeof onCapture === 'function') {
          if (autoSubmitForm) {
            // add small delay after setting parameter values before
            // submitting form
            setTimeout(() => {
              submitForm();
            }, 10);
          }
        }
      }
    },
    [autoSubmitForm, setParameterValue, submitForm]
  );

  return onCapture;
};

const WaypointFormCapture = (props: WaypointFormCaptureProps): null => {
  const { autoSubmitForm } = props;
  const onCaptureUpdateForm = useUpdateFlowItemFormOnWaypointCapture({
    autoSubmitForm,
  });

  const onCapture = useCallback(
    (waypoint: Waypoint, qnear: QNear) => {
      onCaptureUpdateForm(waypoint, qnear);
      if (props.onCapture) {
        props.onCapture(waypoint, qnear);
      }
    },
    [onCaptureUpdateForm, props]
  );

  useWaypointCapture({ ...props, onCapture });
  return null;
};

export {
  useUpdateFlowItemFormOnWaypointCapture,
  useWaypointCapture,
  useWaypointCaptureBinding,
  WaypointCaptureContextProvider,
  WaypointFormCapture,
};
